import type { UniversalSearchMarketResult, UniversalSearchResult, UniversalSearchResultKind } from '@einhaus/project314-types';
import { getEntityLink } from '@utils/entityHelpers';

type UniversalSearchGroupKey = 'markets' | Exclude<UniversalSearchResultKind, UniversalSearchMarketResult['kind']>;

const GROUP_LABELS: Record<UniversalSearchGroupKey, string> = {
    markets: 'Stocks, indexes & crypto',
    tradingStrategy: 'Strategies',
    portfolio: 'Portfolios',
    optionBuyingStrategy: 'Option strategies',
    tradingProfile: 'Trading profiles',
    scorecard: 'Scorecards',
    screener: 'Screeners',
    strategyFamily: 'Strategy families',
    experiment: 'Experiments'
};

const KIND_LABELS: Record<UniversalSearchResultKind, string> = {
    Asset: 'Stock',
    Cryptocurrency: 'Crypto',
    AssetIndex: 'Index',
    tradingStrategy: 'Strategy',
    portfolio: 'Portfolio',
    optionBuyingStrategy: 'Option strategy',
    tradingProfile: 'Trading profile',
    scorecard: 'Scorecard',
    screener: 'Screener',
    strategyFamily: 'Strategy family',
    experiment: 'Experiment'
};

const SEARCHABLE_ID_PATTERN = /^#?\d+$/;

export interface UniversalSearchResultDetails {
    title: string;
    subtitle: string | null;
    kindLabel: string;
    badges: string[];
}

export interface UniversalSearchResultGroup {
    key: UniversalSearchGroupKey;
    label: string;
    /** Each result with its position in the flat result list, which keyboard highlighting uses. */
    items: { result: UniversalSearchResult; index: number }[];
}

function isMarketResult(result: UniversalSearchResult): result is UniversalSearchMarketResult {
    return result.kind === 'Asset' || result.kind === 'Cryptocurrency' || result.kind === 'AssetIndex';
}

/** Short IDs like `7` are worth searching; other one-character terms match too much to be useful. */
export function isSearchableUniversalSearchTerm(term: string): boolean {
    return term.length >= 2 || SEARCHABLE_ID_PATTERN.test(term);
}

export function getUniversalSearchResultKey(result: UniversalSearchResult): string {
    return `${result.kind}-${result.id}`;
}

export function getUniversalSearchResultPath(result: UniversalSearchResult): string {
    switch (result.kind) {
        case 'Asset':
        case 'Cryptocurrency':
        case 'AssetIndex':
            return getEntityLink({ entityId: result.id, entityType: result.kind });
        case 'tradingStrategy':
            return `/user/tradingStrategy/${result.id}`;
        case 'portfolio':
            return `/user/portfolio/${result.id}`;
        case 'optionBuyingStrategy':
            return `/user/optionBuyingStrategy/${result.id}`;
        case 'tradingProfile':
            return `/user/portfolioTradingProfile/${result.id}`;
        case 'scorecard':
            return `/user/scorecard/${result.id}`;
        case 'screener':
            return `/tools/screener/${result.id}`;
        case 'strategyFamily':
            return `/user/strategyFamilies/${result.id}`;
        case 'experiment':
            // Matches the Experiments list: finished runs open their results, everything else the editor.
            return result.executionStatus === 'completed' ? `/user/experimentResults/${result.id}` : `/user/experiment/${result.id}`;
        default: {
            const unhandled: never = result;
            throw new Error(`Unhandled universal search result: ${JSON.stringify(unhandled)}`);
        }
    }
}

function getResultBadges(result: UniversalSearchResult): string[] {
    const badges: string[] = [];

    switch (result.kind) {
        case 'tradingStrategy':
        case 'optionBuyingStrategy':
            if (!result.active) badges.push('Inactive');
            if (result.sharedWithUser) badges.push('Shared with you');
            break;
        case 'tradingProfile':
        case 'scorecard':
            if (result.sharedWithUser) badges.push('Shared with you');
            break;
        case 'portfolio':
            badges.push(result.portfolioType === 'live' ? 'Live' : 'Paper');
            if (!result.active) badges.push('Inactive');
            break;
        case 'screener':
            if (result.isDraft) badges.push('Draft');
            if (!result.isLatestRevision) badges.push('Older revision');
            break;
        case 'experiment':
            badges.push(result.executionStatus.charAt(0).toUpperCase() + result.executionStatus.slice(1));
            break;
        default:
            break;
    }

    return badges;
}

export function getUniversalSearchResultDetails(result: UniversalSearchResult): UniversalSearchResultDetails {
    const kindLabel = KIND_LABELS[result.kind];

    if (isMarketResult(result)) {
        return { title: result.symbol, subtitle: result.symbol === result.name ? null : result.name, kindLabel, badges: [] };
    }

    const revision = result.kind === 'screener' ? ` · Revision ${result.revision}` : '';
    return { title: result.name, subtitle: `#${result.id}${revision}`, kindLabel, badges: getResultBadges(result) };
}

/** Groups the backend's already-ordered results into consecutive sections; market kinds share one section. */
export function groupUniversalSearchResults(results: UniversalSearchResult[]): UniversalSearchResultGroup[] {
    const groups: UniversalSearchResultGroup[] = [];

    results.forEach((result, index) => {
        const key: UniversalSearchGroupKey = isMarketResult(result) ? 'markets' : result.kind;
        const lastGroup = groups.at(-1);

        if (lastGroup?.key === key) {
            lastGroup.items.push({ result, index });
        } else {
            groups.push({ key, label: GROUP_LABELS[key], items: [{ result, index }] });
        }
    });

    return groups;
}
